"use client";

import { cn } from "@/lib/utils/general-utils";
import type { ReactNode } from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

const ALLOWED_ELEMENTS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "a",
] as const;

type MdNode = {
  type: string;
  children?: MdNode[];
};

function headingsToParagraphs() {
  return (tree: MdNode) => {
    const visit = (node: MdNode) => {
      if (!node.children) return;
      for (const child of node.children) {
        if (child.type === "heading") {
          child.type = "paragraph";
        }
        visit(child);
      }
    };
    visit(tree);
  };
}

function isHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function urlTransform(url: string): string {
  const trimmed = url.trim();
  return isHttpUrl(trimmed) ? trimmed : "";
}

function MarkdownLink({
  href,
  children,
}: {
  readonly href?: string;
  readonly children?: ReactNode;
}) {
  if (!href || !isHttpUrl(href)) {
    return <span>{children}</span>;
  }

  return (
    <a
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </a>
  );
}

const components: Components = {
  a: MarkdownLink,
};

export interface InlineMarkdownProps {
  readonly text: string;
  readonly className?: string;
}

export function InlineMarkdown({ text, className }: InlineMarkdownProps) {
  return (
    <div
      className={cn(
        "min-w-0 break-words [&_p]:m-0 [&_p+p]:mt-2 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline [&_a]:underline-offset-2",
        className,
      )}
    >
      <ReactMarkdown
        skipHtml
        allowedElements={[...ALLOWED_ELEMENTS]}
        remarkPlugins={[headingsToParagraphs, remarkBreaks]}
        urlTransform={urlTransform}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
